import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../utils/api'
import { useAuthStore } from '../store/authStore'

/**
 * Fetches activation status (post-onboarding checklist for /welcome page).
 * Poll interval defaults to 30s — cheap call, auto-updates when the user
 * creates a collection / adds a profile in another tab.
 *
 * After complete() or dismiss() we sync authStore so default redirects and
 * sidebar visibility react immediately without waiting for /me refresh.
 */
export function useActivation({ pollMs = 30000 } = {}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const timerRef = useRef(null)
  const setUser = useAuthStore.setState

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/users/activation/status')
      setData(res.data?.data || null)
      setError(null)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    if (pollMs > 0) {
      timerRef.current = setInterval(fetchStatus, pollMs)
      return () => clearInterval(timerRef.current)
    }
  }, [fetchStatus, pollMs])

  const complete = useCallback(async () => {
    const res = await api.post('/api/users/activation/complete')
    const activationCompletedAt = res.data?.data?.activationCompletedAt
    setUser((state) => ({
      user: state.user ? { ...state.user, activationCompletedAt } : null,
    }))
    await fetchStatus()
    return activationCompletedAt
  }, [fetchStatus, setUser])

  const dismiss = useCallback(async () => {
    const res = await api.post('/api/users/activation/dismiss')
    const activationDismissedAt = res.data?.data?.activationDismissedAt
    setUser((state) => ({
      user: state.user ? { ...state.user, activationDismissedAt } : null,
    }))
    await fetchStatus()
    return activationDismissedAt
  }, [fetchStatus, setUser])

  return { data, loading, error, refetch: fetchStatus, complete, dismiss }
}

export default useActivation
